import { useEffect, useRef, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

export const useWebSocket = (isPublic, publicConfig) => {
    const [isConnected, setIsConnected] = useState(false)
    const [lastMessage, setLastMessage] = useState(null)
    const [error, setError] = useState(null)
    const socketRef = useRef(null)
    const reconnectTimeoutRef = useRef(null)
    const reconnectAttemptsRef = useRef(0)
    const connectionIdRef = useRef(uuidv4())
    const hasConnectedRef = useRef(false) // Track if we've connected once

    // Get auth data from Redux
    const token = useSelector((state) => state.auth?.token)
    const userID = useSelector((state) => state.auth?.userID)
    const workspaceID = useSelector((state) => state.auth?.workspaceID)

    const connect = useCallback(() => {
        // Prevent multiple connections
        if (
            socketRef.current?.readyState === WebSocket.OPEN ||
            socketRef.current?.readyState === WebSocket.CONNECTING
        ) {
            return
        }

        try {
            const wsUrl = process.env.REACT_APP_WS_ENDPOINT
            const socket = new WebSocket(wsUrl)
            socketRef.current = socket

            socket.onopen = () => {
                // console.log('WebSocket connected for workspace:', workspaceID)
                setIsConnected(true)
                setError(null)
                reconnectAttemptsRef.current = 0
                hasConnectedRef.current = true
            }
            socket.onclose = (event) => {
                // console.log('WebSocket disconnected', event.code, event.reason)
                setIsConnected(false)

                // Attempt reconnection
                if (reconnectAttemptsRef.current < 5) {
                    const timeout = Math.min(
                        1000 * Math.pow(2, reconnectAttemptsRef.current),
                        30000
                    )
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current++
                        connect()
                    }, timeout)
                }
            }
            socket.onerror = (error) => {
                // console.error('WebSocket error:', error)
                setError(error)
            }
            socket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data)
                    setLastMessage(message)
                } catch (err) {
                    // console.error('Failed to parse message:', err)
                }
            }
        } catch (err) {
            // console.error('Failed to create WebSocket:', err)
            setError(err)
        }
    }, [workspaceID, userID, token]) // Dependencies to ensure fresh values are used

    const sendMessage = useCallback(
        (message) => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                // Automatically inject auth data and connectionID
                // Remove any userID, workspaceID, token from the message to avoid duplicates
                const {
                    userID: msgUserID,
                    workspaceID: msgWorkspaceID,
                    token: msgToken,
                    ...restMessage
                } = message

                const enrichedMessage = {
                    ...restMessage,
                    connectionID: connectionIdRef.current,
                    userID: userID,
                    workspaceID: workspaceID,
                    token: isPublic && publicConfig ? publicConfig.token : token,
                }
                socketRef.current.send(JSON.stringify(enrichedMessage))
                return true
            }
            return false
        },
        [userID, workspaceID, token, isPublic, publicConfig]
    )

    // Initial connection effect
    useEffect(() => {
        // Only connect if we haven't connected yet
        if (!hasConnectedRef.current) {
            connect()
        }

        return () => {
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
            if (socketRef.current) {
                socketRef.current.close()
                hasConnectedRef.current = false
            }
        }
    }, [connect])

    // Workspace change detection - disconnect when workspace changes
    useEffect(() => {
        // If we have an active connection and workspace changes, close it
        // The key-based reset will handle remounting with fresh state
        return () => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                // console.log('Closing socket due to workspace change')
                socketRef.current.close()
            }
        }
    }, [workspaceID])

    const clearLastMessage = useCallback(() => {
        setLastMessage(null)
    }, [])

    return {
        isConnected,
        lastMessage,
        sendMessage,
        error,
        connectionState: socketRef.current?.readyState,
        connectionId: connectionIdRef.current,
        clearLastMessage,
    }
}
